<template>
  <section
    v-if="!(basetype == 2 && !freeSpots)"
    class="tours-item"
  >
    <header
      class="tours-item__header"
      :class="{
        'is-open': open
      }"
    >
      <span>
        <strong>{{ formattedTime }}</strong>
        {{ $t('time') }}
      </span>

      <span class="tours-item__name text-quotes">{{ name }}</span>
      <span v-if="freeSpots">{{ freeSpots }} {{ $tc('spotsFree', freeSpots) }}</span>

      <c-action
        v-if="freeSpots"
        class="tours-item__toggle"
        :color="buttonColor"
        :disabled="!freeSpots"
        :aria-expanded="open"
        @click="toggleOpen"
      >
        {{ $t('expand') }}
      </c-action>

      <span v-else>{{ $t('spotsFull') }}</span>
    </header>

    <c-transition-height>
      <div v-if="open">
        <div class="tours-item__content">
          <div v-html="description" />

          <div class="tours-item__persons">
            <div
              v-if="hasVIPBooking"
              class="tours-item__vip"
            >
              <c-action
                color="secondary"
                @click="addVIPToCart"
              >
                <c-icon type="star" />
                {{ $t('bookVIP') }}
              </c-action>

              <c-tooltip>
                <c-icon
                  type="info"
                  size="1.5"
                />

                <template #popup>
                  {{ getLabel('introduction.tours_select_vip') }}
                </template>
              </c-tooltip>
            </div>

            <p
              v-if="minPerTour > 1"
              class="tours-item__min-per-tour"
            >
              {{ $t('minPerTour') }}: {{ minPerTour }}
            </p>

            <c-form-select
              v-for="type in types"
              :key="type.id"
              v-model.number="persons[type.id]"
              color="white"
              inline-label
            >
              {{ prices[type.id][`text_${$i18n.locale}`] }}
              {{ getPrice(type.id) }} €

              <template
                v-if="freeSpots"
                #options
              >
                <option
                  value="0"
                >
                  0
                </option>
                <option
                  v-for="n in remainingSpots + persons[type.id]"
                  :key="n"
                  :value="n"
                >
                  {{ n }}
                </option>
              </template>
            </c-form-select>

            <c-action
              color="primary"
              :disabled="!selectedSpots"
              @click="addToCart"
            >
              {{ $t('addToCart') }}
              <c-icon
                type="chevron-right"
                :size="0.75"
              />
            </c-action>
          </div>
        </div>
      </div>
    </c-transition-height>
  </section>
</template>

<script>
import CAction from './action.vue'
import CFormSelect from './formSelect.vue'
import CIcon from './icon.vue'
import CTooltip from './tooltip.vue'
import CTransitionHeight from './transitionHeight.vue'

import eventBus from '../eventBus.js'
import priceHelper from '../functions/priceHelper.js'

import labelMixin from '../mixins/label.js'

import { GAP_ALL_DAY } from '../settings.js'

const TODAY = new Date()

export default {
  name: 'CToursItem',

  components: {
    CAction,
    CFormSelect,
    CIcon,
    CTooltip,
    CTransitionHeight
  },

  mixins: [labelMixin],

  props: {
    gap: {
      type: Number,
      required: true
    },
    time: {
      type: String,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    description: {
      type: String,
      default: ''
    },
    freeSpots: {
      type: Number,
      required: true
    },
    soldSpots: {
      type: Number,
      required: true
    },
    priceGroups: {
      type: Array,
      required: true
    },
    priceDeviation: {
      type: Number,
      required: true
    },
    basetype: {
      type: Number,
      required: true
    },
    typ: {
      type: Number,
      required: true
    },
    cls: {
      type: Number,
      required: true
    }
  },

  data () {
    return {
      open: false,
      persons: {}
    }
  },

  computed: {
    selectedSpots () {
      return Object
        // Get the selected spots for each person type.
        .values(this.persons)
        // Add the selected spots together.
        .reduce((accumulator, value) => accumulator + value, 0)
    },
    remainingSpots () {
      const selectedTicketsAmount = this.$store.state.selectedTicketsAmount
      const overbookingAmount = this.$store.state.toursConfig.overbooking_leer

      let remainingSpots = this.freeSpots - this.selectedSpots

      // Add overbooking if available:
      // * All spots have to be available (no spots sold).
      // * The number of selected persons has to equal the available spots plus the overbooking amount.
      if (!this.soldSpots && this.freeSpots === (selectedTicketsAmount - overbookingAmount)) {
        remainingSpots += overbookingAmount
      }

      return remainingSpots
    },
    formattedTime () {
      return this.time.slice(0, -3)
    },
    buttonColor () {
      if (this.open) {
        return 'secondary-dark'
      }

      return 'tertiary'
    },
    prices () {
      return this.$store.state.articles
    },
    types () {
      const types = []

      for (const id of this.priceGroups) {
        types.push(this.prices[id])
      }

      return types
    },
    hasVIPBooking () {
      if (this.basetype === 2) {
        // no VIP booking for Yullbe
        return false
      }

      if (this.soldSpots) {
        return false
      }

      if (this.$store.state.selectedTicketsAmount > this.freeSpots) {
        return false
      }

      return true
    },
    minPerTour () {
      return this.$store.state.toursConfig.teilnehmer_min
    }
  },

  created () {
    this.initPersons()
    this.setInitialOpen()
  },

  methods: {
    initPersons () {
      this.types.forEach(type => {
        this.$set(this.persons, type.id, 0)
      })
    },

    setInitialOpen () {
      if (!this.freeSpots) {
        return
      }

      const selectedTime = this.$store.state.toursSelectedTime
      const selectedDate = this.$store.state.ticketSelection.selectedDate
      const tourTime = new Date(selectedDate)
      tourTime.setHours(this.time.slice(0, 2), this.time.slice(3, 5))

      if (tourTime >= selectedTime.start && tourTime < selectedTime.end) {
        this.open = true
      }
    },

    toggleOpen () {
      this.open = !this.open
    },

    addToCart () {
      const toursFromCart = this.$store.state.cart.tours
      const ticketsFromCart = this.$store.state.cart.tickets
      const selectedDate = this.$store.state.ticketSelection.selectedDate

      const data = {
        date: selectedDate,
        gap: this.gap,
        time: this.time,
        name: this.name,
        // Create a copy of the selected person count, so the form can be reset
        // before the request will be submitted.
        articles: { ...this.persons }
      }

      const addToursToCart = async () => {
        // Reset form first so the desired spot count is selectable again
        // should the cart request fail.
        this.initPersons()

        if (ticketsFromCart.total && ticketsFromCart.gap !== GAP_ALL_DAY) {
          await addTicketsToCart()
        }

        const response = await this.$store.dispatch('addToursTicketsToCart', data)
        this.$store.commit('showContainerSidebar', true)

        // Only close if the tour has been successfully added to the cart.
        if (response?.status === 200) {
          this.open = false
        }
      }

      const addTicketsToCart = async () => {
        const gap = { gap: GAP_ALL_DAY, date: selectedDate }
        await this.$store.dispatch('addTicketsToCart', { gap })
      }

      const replaceCart = async () => {
        await addTicketsToCart()
        await this.$store.dispatch('removeFromCart', { endpoint: 'tours' })

        addToursToCart()
      }

      if (toursFromCart.total_articles && toursFromCart.date !== selectedDate) {
        eventBus.emit('showMessage', {
          title: this.$t('replace.title'),
          message: this.$t('replace.message'),
          buttons: [
            {
              label: this.$t('replace.replace'),
              color: 'primary',
              callback: replaceCart
            },
            {
              label: this.$t('replace.keep'),
              color: 'secondary'
            }
          ],
          showOk: false
        })
      } else {
        addToursToCart()
      }
    },

    addVIPToCart () {
      this.initPersons()
      this.persons[this.types[0].id] = this.freeSpots
      this.addToCart()
    },

    getPrice (type) {
      const priceDefault = this.prices[type].price
      const priceNew = this.prices[type].price_new
      const priceNewDate = new Date(this.prices[type].price_new_date)

      const price = priceHelper.getArticlePrice(priceDefault, priceNew, priceNewDate, TODAY) + this.priceDeviation

      return priceHelper.format(price / 100, this.$i18n.locale)
    }
  }
}
</script>

<i18n locale="en">
{
  "time": "h",
  "spotsFree": "spot free | spots free",
  "spotsFull": "sold out",
  "expand": "Book",
  "minPerTour": "Minimum persons per tour",
  "addToCart": "Add to cart",
  "bookVIP": "VIP experience",
  "replace": {
    "title": "Replace tours",
    "message": "You already selected experiences for another day. Do you want to replace these experiences?",
    "replace": "Replace",
    "keep": "Keep"
  }
}
</i18n>

<i18n locale="de">
{
  "time": "Uhr",
  "spotsFree": "Platz frei | Plätze frei",
  "spotsFull": "ausgebucht",
  "expand": "Buchung",
  "minPerTour": "Mindestteilnehmer pro Tour",
  "addToCart": "In den Warenkorb",
  "bookVIP": "VIP-Erlebnis",
  "replace": {
    "title": "Erlebnisse ersetzen",
    "message": "Sie haben bereits Erlebnisse für einen anderen Tag ausgewählt. Möchten Sie diese Erlebnisse ersetzen?",
    "replace": "Ersetzen",
    "keep": "Behalten"
  }
}
</i18n>

<style lang="scss" scoped>
@import "../css/variables";

.tours-item {
  border-top: $border-gap;
}

.tours-item__header {
  display: grid;
  grid-template-columns: 6em 1fr;
  align-items: center;
  gap: 0.25em 0.5em;

  min-height: 3em;
  padding: $gutter-xs $gutter-sm;

  &.is-open {
    color: #fff;
    background: $color-secondary;
  }

  @media (prefers-reduced-motion: no-preference) {
    transition:
      color 0.3s,
      background-color 0.3s;
  }

  @media (min-width: $breakpoint-md) {
    grid-template-columns: 6em 1fr 7em 7em;
    align-items: center;
    gap: 1em;

    padding-left: $gutter-md;
    padding-right: $gutter-md;
  }
}

.tours-item__name {
  justify-self: end;

  @media (min-width: $breakpoint-md) {
    justify-self: start;
  }
}

.tours-item__toggle {
  justify-self: end;
}

.tours-item__content {
  display: grid;
  gap: $gutter-sm;
  align-items: start;

  padding: $gutter-sm;

  background: $color-light;

  @media (min-width: $breakpoint-md) {
    grid-template-columns: 1fr 1fr;

    padding-left: $gutter-md;
    padding-right: $gutter-md;
  }
}

.tours-item__persons {
  display: grid;
  justify-items: start;
  gap: $gutter-xs;

  @media (min-width: $breakpoint-sm) {
    justify-items: end;
  }
}

.tours-item__vip {
  display: flex;
  align-items: center;
  gap: 0.5em;

  margin-bottom: $gutter-xs;
}

.tours-item__min-per-tour {
  margin-bottom: 0.5em;
  font-weight: 700;
}
</style>
